import React from "react";
import type { Project, Client } from "../../generated-axios-client";
import { Card, Flex, Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { routeNames } from "../../common/routes";

type Props = {
  projects: Project[];
  clientById: Record<string, Client>;
  loading?: boolean;
  onNavigate: (route: string, id: string) => void;
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export const ProjectsEndingSoonTable: React.FC<Props> = ({
  projects,
  clientById,
  loading,
  onNavigate,
}) => {
  const columns: TableColumnsType<Project> = [
    {
      title: "Project",
      dataIndex: "name",
      key: "name",
      render: (name: string, project: Project) => {
        const projectId = project.id;
        return projectId && name ? (
          <Typography.Link
            style={{ fontSize: 13 }}
            onClick={() => onNavigate(routeNames.PROJECT_VIEW, projectId)}
          >
            {name}
          </Typography.Link>
        ) : (
          <Typography.Text style={{ fontSize: 13 }}>{name ?? "—"}</Typography.Text>
        );
      },
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (clientId: string) => {
        const clientName = clientId ? clientById[clientId]?.legal_name : undefined;
        return clientId && clientName ? (
          <Typography.Link
            style={{ fontSize: 13 }}
            onClick={() => onNavigate(routeNames.CLIENT_VIEW, clientId)}
          >
            {clientName}
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      },
    },
    {
      title: "Ends",
      dataIndex: "end",
      key: "end",
      width: 120,
      render: (end: string) => {
        if (!end) return <Typography.Text type="secondary">—</Typography.Text>;
        const days = daysUntil(end);
        const color = days <= 7 ? "red" : "orange";
        return (
          <Tag color={color} style={{ fontWeight: 600 }}>
            {formatDate(end)}
          </Tag>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <span>Projects Ending Soon</span>
          {!loading && <Tag color="orange">{projects.length}</Tag>}
        </Flex>
      }
      loading={loading}
      style={{ height: "100%" }}
    >
      <Table
        dataSource={projects}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={{
          pageSize: 5,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} projects`,
          size: "small",
          position: ["bottomRight"],
        }}
        locale={{ emptyText: "No projects ending within 30 days" }}
      />
    </Card>
  );
};
